import React from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { Box, Typography, Stack } from "@mui/material";
import { ErrorOutlineRounded as ErrorIcon } from "@mui/icons-material";
import { colors, borderRadiusPx } from "../../../../theme";
import CommonDialog from "./CommonDialog";
import CommonButton from "../inputs/CommonButton";

const FailedPopup = ({
  show,
  open,
  isAdmin,
  setShowModal,
  onClose,
  message = "Oops, something went wrong!",
  title = "Transaction Failed",
  description = "The transaction was rejected or encountered an on-chain error. Please verify your wallet balance and try again.",
  lotteryType,
  btnName = "Close",
  onRetry,
  showCloseButton = true,
}) => {
  const history = useHistory();
  const isOpen = Boolean(
    show !== undefined ? show : open !== undefined ? open : isAdmin,
  );

  const handleClose = () => {
    if (lotteryType === "revolver") {
      if (typeof setShowModal === "function") setShowModal(false);
      if (typeof onClose === "function") onClose();
      window.location.reload();
      history.push("/");
    } else {
      if (typeof setShowModal === "function") setShowModal(false);
      if (typeof onClose === "function") onClose();
    }
  };

  return (
    <CommonDialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      dividers={false}
      showCloseButton={showCloseButton}
      PaperProps={{
        sx: {
          borderRadius: borderRadiusPx.xl,
          p: { xs: 2, sm: 2.5 },
          textAlign: "center",
        },
      }}
    >
      <Box sx={{ pt: 1, pb: 1, px: { xs: 1, sm: 2 } }}>
        {/* Error Icon Badge */}
        <Box
          sx={{
            width: 68,
            height: 68,
            borderRadius: borderRadiusPx.circle,
            background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2.5,
            boxShadow: "0 8px 22px rgba(239, 68, 68, 0.35)",
          }}
        >
          <ErrorIcon sx={{ fontSize: 38 }} />
        </Box>

        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            color: colors.slate[900],
            mb: 1,
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>

        {/* Message */}
        <Typography
          variant="body1"
          sx={{
            color: colors.slate[700],
            fontWeight: 600,
            lineHeight: 1.5,
            mb: 1,
          }}
        >
          {message}
        </Typography>

        {/* Description / Advice */}
        {description && (
          <Typography
            variant="body2"
            sx={{
              color: colors.slate[400],
              lineHeight: 1.45,
              mb: 2.5,
            }}
          >
            {description}
          </Typography>
        )}

        {/* Action Buttons */}
        <Box sx={{ mt: 1 }}>
          {onRetry ? (
            <Stack direction="row" spacing={1.5}>
              <CommonButton
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleClose}
                label="Cancel"
                sx={{
                  py: 1.35,
                  borderRadius: borderRadiusPx.lg,
                }}
              />
              <CommonButton
                fullWidth
                variant="primary"
                size="large"
                onClick={() => {
                  handleClose();
                  onRetry();
                }}
                label="Try Again"
                sx={{
                  py: 1.35,
                  borderRadius: borderRadiusPx.lg,
                }}
              />
            </Stack>
          ) : (
            <CommonButton
              fullWidth
              variant="dangerSolid"
              size="large"
              onClick={handleClose}
              label={btnName}
              sx={{
                py: 1.35,
                fontWeight: 700,
                fontSize: "0.95rem",
                borderRadius: borderRadiusPx.lg,
              }}
            />
          )}
        </Box>
      </Box>
    </CommonDialog>
  );
};

FailedPopup.propTypes = {
  show: PropTypes.bool,
  open: PropTypes.bool,
  isAdmin: PropTypes.bool,
  setShowModal: PropTypes.func,
  onClose: PropTypes.func,
  message: PropTypes.node,
  title: PropTypes.node,
  description: PropTypes.node,
  lotteryType: PropTypes.string,
  btnName: PropTypes.string,
  onRetry: PropTypes.func,
  showCloseButton: PropTypes.bool,
};

export default React.memo(FailedPopup);
